/**
 * Service Providers Page
 * Marketing page for waste management service providers explaining CalWMP's value proposition
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export function ServiceProviders() {
  // Update page title and meta description for SEO
  useEffect(() => {
    document.title = 'For Service Providers - CalWMP | NOT A BROKER - Direct Customer Relationships';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Built by a hauler for haulers. CalWMP is NOT a broker—connect directly with contractors via Stripe Connect (1% platform fee). Own the customer relationship. AI eliminates busy work. No broker markups, no payment delays.'
      );
    }

    // Add keywords meta tag for SEO
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'waste management service providers, hauling companies California, C&D recycling, construction waste haulers, material recovery facilities, roll-off container companies, demolition contractors, waste diversion services'
    );
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center py-12">
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-50 border border-red-300 mb-6">
          <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <span className="text-sm font-bold text-red-900">WE ARE NOT A BROKER</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4 leading-tight">
          Built by a Hauler for Haulers
          <br />
          <span className="text-primary-600">Your Customers. Your Payments. Your Business.</span>
        </h1>
        <p className="text-xl text-neutral-600 mb-2 max-w-3xl mx-auto">
          We connect you directly with contractors—no middleman, no upcharges, no delayed payments. You own the customer relationship. We just eliminate the busy work.
        </p>
        <p className="text-lg text-neutral-500 mb-8">
          Waste haulers • Recycling facilities • Material recovery facilities • C&D recyclers
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/provider/request-invite" className="btn-primary">
            Request Invite
          </Link>
          <a href="#why-different" className="btn-secondary">
            See Why We&apos;re Different
          </a>
        </div>
        <p className="text-sm text-neutral-600 mt-4">
          Already have an invite code?{' '}
          <Link to="/provider/register" className="text-primary-600 hover:text-primary-700 font-medium">
            Register here
          </Link>
        </p>
      </div>

      {/* Why We're Different Section */}
      <div className="my-16 card bg-gradient-to-br from-primary-50 to-accent-50 border-primary-200" id="why-different">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Why We&apos;re Different from Brokers
          </h2>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            We&apos;re service providers who built technology to help other service providers. Not a broker trying to insert ourselves between you and your customers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card bg-white border-2 border-primary-300">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">Traditional Brokers</h3>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Insulate customers from you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Collect payment with upcharges</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Delay payments to you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Own the customer relationship</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Create dependency on their platform</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card bg-white border-2 border-accent-400">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">CalWMP Platform</h3>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li className="flex items-start">
                    <span className="text-accent-600 mr-2">✓</span>
                    <span><strong>Direct customer contact</strong> from day one</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent-600 mr-2">✓</span>
                    <span><strong>Get paid via Stripe Connect</strong>—fast, secure, transparent (1% platform fee)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent-600 mr-2">✓</span>
                    <span><strong>No broker markup</strong>—you set your prices, we don&apos;t touch the money</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent-600 mr-2">✓</span>
                    <span><strong>You own the relationship</strong>—build repeat business</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-accent-600 mr-2">✓</span>
                    <span><strong>AI handles busy work</strong>—weight tickets, compliance docs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center card bg-neutral-50 border-neutral-300">
          <p className="text-neutral-700">
            <strong>Our founder is a former hauler and technologist</strong> who understands your frustrations with clunky software, endless paperwork, and platforms that work against you. CalWMP uses AI to streamline bidding, automate compliance documentation, and eliminate the busy work—so you can focus on servicing customers.
          </p>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="my-16">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-4">
          How CalWMP Helps Your Business
        </h2>
        <p className="text-center text-neutral-600 mb-10 max-w-2xl mx-auto">
          Spend less time on paperwork and phone tag. More time servicing customers and growing your business.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Secure Payment Processing</h3>
            <p className="text-neutral-600">
              Get paid through Stripe Connect—the industry standard used by platforms like Shopify and DoorDash. We add a 1% platform fee to your quoted price. No hidden fees, no broker markups, no payment delays.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">AI Streamlines Bidding</h3>
            <p className="text-neutral-600">
              AI helps you quickly bid on work. High-quality CalGreen plans arrive with materials identified, quantities estimated, and requirements clear.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Compliance Made Easy</h3>
            <p className="text-neutral-600">
              AI handles data obligations like weight ticket tracking and compliance paperwork. Stop wasting hours on documentation—focus on the work.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Own the Relationship</h3>
            <p className="text-neutral-600">
              Work directly with contractors from day one. Build relationships, earn repeat business, and grow your customer base—not someone else&apos;s.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Local Projects Only</h3>
            <p className="text-neutral-600">
              Get matched with projects in your service area. No time wasted on opportunities outside your coverage—only qualified local leads.
            </p>
          </div>

          <div className="card">
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Growing Market</h3>
            <p className="text-neutral-600">
              California&apos;s strict 65% diversion requirements mean increasing demand. Position yourself as a trusted partner on a compliance platform contractors rely on.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="my-16" id="how-it-works">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-4">
          How It Works
        </h2>
        <p className="text-center text-neutral-600 mb-10 max-w-2xl mx-auto">
          From email notification to winning the bid—here&apos;s how CalWMP connects you with contractors.
        </p>

        <div className="space-y-8">
          {/* Step 1 */}
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">1</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Join Our Network</h3>
              <p className="text-neutral-600 mb-3">
                Sign up as a verified service provider. Specify your service types (hauling, recycling, MRF, etc.) and coverage areas across California counties.
              </p>
              <div className="card bg-neutral-50 border-neutral-300">
                <p className="text-sm text-neutral-700">
                  <strong>Service Types:</strong> Waste Hauling • Roll-off Containers • C&D Recycling • Material Recovery • Demolition Services • Specialty Recycling
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">2</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Receive Project Notifications</h3>
              <p className="text-neutral-600 mb-3">
                When a contractor creates a plan in your service area, you&apos;ll receive an email with project details: location, material types, estimated quantities, and timeline.
              </p>
              <div className="card bg-primary-50 border-primary-200">
                <p className="text-sm text-primary-900 font-medium mb-2">
                  Sample Email Notification:
                </p>
                <p className="text-sm text-neutral-700">
                  &quot;New CalGreen project in San Diego County: Commercial demolition, 50 tons mixed C&amp;D debris, 75% diversion target. Materials: concrete, metal, wood. Project start: March 2025.&quot;
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">3</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Submit Your Bid with AI Assistance</h3>
              <p className="text-neutral-600 mb-3">
                Review project details and submit competitive pricing. AI helps you quickly estimate costs and generate professional bids—no more manual calculations or guesswork.
              </p>
              <ul className="text-sm text-neutral-600 space-y-2">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  AI-powered bid templates and cost estimation
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Direct communication with contractor (no middleman)
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Simple 1% platform fee (added to customer total, not deducted from your quote)
                </li>
              </ul>
            </div>
          </div>

          {/* Step 4 */}
          <div className="flex gap-6 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-accent-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">4</span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Win the Job & Get Paid via Stripe</h3>
              <p className="text-neutral-600 mb-3">
                Contractor reviews bids and selects you. Coordinate directly with them, deliver your services, and get paid through Stripe Connect. AI handles compliance documentation—weight tickets, reporting, and CalGreen paperwork.
              </p>
              <div className="card bg-accent-50 border-accent-200">
                <p className="text-sm text-neutral-700">
                  <strong>Secure, fast payments through Stripe Connect.</strong> Customer pays via the platform (your quote + 1% platform fee). You receive your full quoted amount directly to your Stripe account within 2 business days. No broker markups, no payment delays. Your customer relationship, your business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="my-16">
        <h2 className="text-3xl font-bold text-neutral-900 text-center mb-10">
          Common Questions
        </h2>

        <div className="space-y-6">
          <div className="card bg-red-50 border-red-300">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Are you a broker?
            </h3>
            <p className="text-neutral-600">
              <strong>No. We are NOT a broker.</strong> Brokers insert themselves between you and customers, collect payments, delay payouts, and own the customer relationship. CalWMP connects you directly with contractors—you communicate directly, negotiate directly, and get paid directly. We&apos;re service providers who built technology to help other service providers, not a middleman trying to profit from your work.
            </p>
          </div>

          <div className="card bg-accent-50 border-accent-300">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              How do payments work? What fees do you charge?
            </h3>
            <p className="text-neutral-600 mb-3">
              <strong>Payments are processed through Stripe Connect</strong>—the same trusted platform used by Shopify, DoorDash, and thousands of marketplaces. When a customer pays, we add a <strong>1% platform fee</strong> to your quoted price, which is clearly disclosed to the customer. You receive your full quoted amount directly to your Stripe account, typically within 2 business days.
            </p>
            <p className="text-neutral-600">
              <strong>Example:</strong> You quote $1,000 for a job. Customer pays $1,010 (your $1,000 + our $10 platform fee). You receive $1,000. No broker markups on your pricing, no hidden fees, no payment delays.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Do I work directly with the customer or through CalWMP?
            </h3>
            <p className="text-neutral-600">
              <strong>You work directly with customers from day one.</strong> When you&apos;re selected for a project, you coordinate directly with the contractor—site visits, scheduling, delivery, invoicing, everything. You own the relationship and can earn repeat business. CalWMP just facilitates the initial connection and provides AI tools to help with compliance paperwork.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              What types of service providers can join?
            </h3>
            <p className="text-neutral-600">
              We welcome all California waste management professionals including: waste hauling companies, recycling facilities, material recovery facilities (MRFs), C&D recycling centers, demolition contractors with hauling services, roll-off container companies, and specialty recyclers.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              How much does it cost to join?
            </h3>
            <p className="text-neutral-600 mb-3">
              CalWMP uses a <strong>subscription-based pricing model</strong> with tiers based on the tools and features you use. All plans include the 1% platform fee on transactions processed through Stripe Connect.
            </p>
            <p className="text-neutral-600">
              <strong>Basic Tier (Free):</strong> Lead notifications, direct customer contact, basic bidding tools<br />
              <strong>Pro Tier:</strong> AI-powered bid assistance, compliance automation, weight ticket tracking<br />
              <strong>Enterprise Tier:</strong> Custom integrations, dedicated support, advanced analytics
            </p>
            <p className="text-neutral-600 mt-3 text-sm">
              Pricing details will be finalized during beta. Early adopters get preferential pricing locked in for 12 months.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              What information do I need to provide?
            </h3>
            <p className="text-neutral-600">
              Basic business information including your company name, service types, coverage areas (by county), contact information, and any relevant certifications or permits. You can also add details about specializations, equipment, and unique capabilities.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              How are contractors matched with service providers?
            </h3>
            <p className="text-neutral-600">
              When a contractor creates a plan, we identify service providers based on: (1) service area match (provider covers the project county), (2) service type match (provider handles the needed materials/services), and (3) provider preferences for project size and type.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              Can I control which projects I see?
            </h3>
            <p className="text-neutral-600">
              Yes. You can set your notification preferences including counties served, minimum/maximum project sizes, specific material types you specialize in, and how frequently you want to receive notifications. Update these anytime in your provider dashboard.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              What happens if I win a bid but can&apos;t fulfill it?
            </h3>
            <p className="text-neutral-600">
              While we expect professional commitment, we understand circumstances change. If you must withdraw, notify the contractor immediately through the platform. Repeated withdrawals may impact your standing in our network, as we prioritize reliable service providers.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              How does CalWMP verify service providers?
            </h3>
            <p className="text-neutral-600">
              We verify basic business credentials including licensing, insurance, and facility permits where applicable. As our network grows, we&apos;ll introduce provider ratings and reviews from contractors to help build trust and transparency.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="card bg-gradient-to-br from-primary-50 to-accent-50 border-primary-200 my-16">
        <div className="text-center py-8">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Ready to Work Smarter, Not Harder?
          </h2>
          <p className="text-xl text-neutral-600 mb-4 max-w-2xl mx-auto">
            Join service providers who are spending less time on paperwork and more time growing their business.
          </p>
          <p className="text-lg text-primary-700 font-semibold mb-8">
            Direct relationships. Direct payments. Your business.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/provider/request-invite" className="btn-primary text-lg px-8 py-3">
              Request Invite
            </Link>
            <Link to="/docs/faq" className="btn-secondary text-lg px-8 py-3">
              More Questions?
            </Link>
          </div>
          <p className="text-sm text-neutral-500 mt-6">
            Questions? Email us at <a href="mailto:providers@calwmp.com" className="text-primary-600 hover:text-primary-700 font-medium">providers@calwmp.com</a>
          </p>
        </div>
      </div>

      {/* Trust Section */}
      <div className="text-center py-8 border-t border-neutral-200">
        <p className="text-sm text-neutral-600 max-w-2xl mx-auto">
          CalWMP is an independent service platform connecting California construction professionals with waste management providers. We are not affiliated with any government agency or regulatory body. Our mission is to make CalGreen compliance easier for everyone involved.
        </p>
      </div>
    </div>
  );
}
